// TimePavilion access control — single source of truth for what the
// Control Panel can grant or hide, and for how the app enforces it.
//
// A user document carries:
//   permissions: {
//     hiddenPages:    ['/schedule', ...],          // routes removed from sidebar + blocked
//     hiddenFeatures: ['dashboard.quickActions'],  // page sections/actions hidden inside pages
//   }
// Admins (Vahid) are never restricted.

export const PAGE_REGISTRY = [
  { path: '/dashboard', label: 'Dashboard' },
  { path: '/schedule', label: 'Schedule' },
  { path: '/time-tracking', label: 'Time Tracking' },
  { path: '/task-v2', label: 'Tasks' },
  { path: '/social-media', label: 'Social Media' },
  { path: '/production-line', label: 'Production Line' },
  { path: '/scenario', label: 'Scenario' },
  { path: '/city-editor', label: 'City Editor' },
  { path: '/asset-library', label: 'Asset Library' },
  { path: '/team', label: 'Team' },
  { path: '/settings', label: 'Settings' },
];

// Feature keys are namespaced by page. Adding an entry here makes it
// toggleable in the Control Panel; enforce it in the page with can().
export const FEATURE_REGISTRY = {
  '/dashboard': [
    { key: 'dashboard.activeWorking', label: 'Active Working Now' },
    { key: 'dashboard.teamPerformance', label: 'Team Performance Analytics' },
    { key: 'dashboard.announcements', label: 'Announcements Feed' },
    { key: 'dashboard.quickActions', label: 'Quick Actions' },
  ],
  '/schedule': [
    { key: 'schedule.addShift', label: 'Add Shift button' },
    { key: 'schedule.teamSchedule', label: 'Team Schedule column' },
  ],
  '/time-tracking': [
    { key: 'timeTracking.teamOverview', label: 'Team Overview tab' },
  ],
  '/task-v2': [
    { key: 'tasks.addStream', label: 'Add Stream button' },
    { key: 'tasks.completePhase', label: 'Complete Phase button' },
  ],
  '/social-media': [
    { key: 'socialMedia.addPost', label: 'Add Post button' },
    { key: 'socialMedia.editPost', label: 'Edit post button' },
    { key: 'socialMedia.deletePost', label: 'Delete post button' },
    { key: 'socialMedia.backupZip', label: 'Backup ZIP export' },
  ],
  '/production-line': [
    { key: 'productionLine.prices', label: 'Prices (screen, PDF & backups)' },
    { key: 'productionLine.quantities', label: 'Quantities / batch counts' },
    { key: 'productionLine.addItem', label: 'Add Item button' },
    { key: 'productionLine.fileMenu', label: 'File menu (save, export, reset)' },
  ],
  '/asset-library': [
    { key: 'assetLibrary.newAsset', label: 'New Asset button' },
    { key: 'assetLibrary.import', label: 'Import' },
    { key: 'assetLibrary.export', label: 'Export' },
    { key: 'assetLibrary.cleanRestore', label: 'Clean Restore' },
  ],
  '/team': [
    { key: 'team.viewDetails', label: 'View Details buttons' },
    { key: 'team.contactInfo', label: 'Phone & email visibility' },
  ],
  '/settings': [
    { key: 'settings.changePin', label: 'Change PIN section' },
  ],
};

export const isAdmin = (user) =>
  Boolean(user && (user.id === 'user-vahid' || user.role === 'admin'));

export const canSeePage = (user, path) => {
  if (!user) return false;
  if (isAdmin(user)) return true;
  const hidden = user.permissions?.hiddenPages || [];
  return !hidden.includes(path);
};

export const can = (user, featureKey) => {
  if (!user) return false;
  if (isAdmin(user)) return true;
  const hidden = user.permissions?.hiddenFeatures || [];
  return !hidden.includes(featureKey);
};

// First page a user is allowed to land on (fallback for blocked routes).
export const firstAllowedPage = (user) => {
  const page = PAGE_REGISTRY.find((p) => canSeePage(user, p.path));
  return page ? page.path : '/dashboard';
};
